import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { getPlanDefinition, hasPlanFeature, isFreePlan } from '../utils/planAccess';

const Navbar = () => {
    const { user, logout } = useAuthStore();
    const location = useLocation();
    const navigate = useNavigate();
    const plan = getPlanDefinition(user?.tenant_plan);

    const isActive = (path) => location.pathname === path;

    const adminLinks = [
        { path: '/admin/dashboard', label: 'Dashboard' },
        { path: '/admin/tables', label: 'Masalar' },
        { path: '/admin/menu', label: 'Menyu' },
        { path: '/warehouse/dashboard', label: 'Anbar', feature: 'warehouse', lockedFor: ['free', 'basic'] },
        { path: '/admin/rental', label: 'Kirayə', feature: 'rental' },
        { path: '/admin/bazarliq', label: 'Bazarlıq', feature: 'purchases' },
        { path: '/admin/expenses', label: 'Xərclər', feature: 'expenses' },
        { path: '/admin/staff', label: 'İşçilər', feature: 'staff' },
        { path: '/admin/kassa-ledger', label: 'Kassa' },
        { path: '/admin/ofis', label: 'Ofis' },
        { path: '/admin/satis', label: 'Satış' },
        { path: '/admin/users', label: 'İstifadəçilər' },
        { path: '/admin/reports', label: 'Hesabatlar', feature: 'reports' },
        { path: '/admin/settings', label: 'Tənzimləmələr', feature: 'branding' }
    ];

    const superLinks = [
        { path: '/superadmin/dashboard', label: 'Dashboard' },
        { path: '/superadmin/tenants', label: 'Restoranlar' }
    ];

    const warehouseLinks = [
        { path: '/admin/menu', label: 'Menyu' },
        { path: '/warehouse/dashboard', label: 'Anbar' },
        { path: '/admin/dashboard', label: 'Adminə qayıt' }
    ];

    const kassirLinks = [
        { path: '/waiter/tables', label: 'Masalar' },
        { path: '/admin/rental', label: 'Kirayə', feature: 'rental' },
    ];

    const activeLinks = user?.role === 'superadmin'
        ? superLinks
        : user?.role === 'anbardar'
            ? warehouseLinks
            : user?.role === 'kassir'
                ? kassirLinks
                : adminLinks;

    const handleLockedClick = () => {
        sessionStorage.setItem('force_upgrade_modal', '1');
        navigate('/admin/dashboard');
        window.dispatchEvent(new CustomEvent('fiscal-open-upgrade-modal'));
    };

    return (
        <nav className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur">
            <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4 lg:px-6">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                        <span className="flex-shrink-0 text-lg font-black tracking-tight text-slate-800 sm:text-xl">
                            Fiscal<span className="text-blue-500"> POS</span>
                        </span>
                        {user?.tenant_name && user?.role !== 'superadmin' && (
                            <span className="max-w-[170px] truncate rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-[11px] font-bold text-primary-700 sm:max-w-[220px] sm:text-xs">
                                {user.tenant_name}
                            </span>
                        )}
                        {plan && user?.role !== 'superadmin' && (
                            <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold sm:text-xs ${plan.badgeClass}`}>
                                {plan.label}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 xl:flex-1 xl:justify-center">
                        {activeLinks.map((link) => {
                            const locked = link.lockedFor?.includes(plan?.code) || (link.feature && !hasPlanFeature(user?.tenant_plan, link.feature));
                            const classes = `rounded-xl px-2.5 py-1.5 text-xs font-semibold transition-all sm:px-3 sm:py-2 sm:text-sm ${
                                isActive(link.path)
                                    ? 'bg-primary-600 text-white shadow-sm'
                                    : locked
                                        ? 'bg-slate-100 text-slate-400'
                                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                            }`;

                            if (locked) {
                                return (
                                    <button key={link.path} type="button" onClick={handleLockedClick} className={classes}>
                                        {link.label} 🔒
                                    </button>
                                );
                            }

                            return (
                                <Link key={link.path} to={link.path} className={classes}>
                                    {link.label}
                                </Link>
                            );
                        })}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 xl:justify-end">
                        <span className="max-w-[180px] truncate text-xs font-medium text-slate-500 sm:text-sm">👤 {user?.name}</span>
                        {user?.role !== 'superadmin' && isFreePlan(user?.tenant_plan) && (
                            <span className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-700 sm:text-xs">
                                Limitli paket
                            </span>
                        )}
                        <button
                            onClick={() => logout(navigate)}
                            className="rounded-xl border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 transition-all hover:bg-red-500 hover:text-white sm:text-sm"
                        >
                            Çıxış
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
